import type { ReactElement } from 'react';
import type { Template } from '@xdocs/schemas';
import { Badge, Button, Card, cn } from '@xdocs/ui';
import { useTemplatesT } from './useTemplatesT.js';
import styles from './TemplateCard.module.css';

export interface TemplateCardProps {
  template: Template;
  onUseTemplate: (template: Template) => void;
}

/**
 * One `/templates` (F1) gallery tile, per FEATURES.md F1's card spec
 * ("Card: name, kind, description, 'Use template'"): name, a kind badge,
 * description (nullable — a muted placeholder line stands in when null,
 * never a literal "null"), and a primary "Use template" CTA. Read-only
 * catalog: no edit/delete affordances — unlike `PersonaCard`, `templates`
 * has no owner to grant those to (`packages/db/src/schema/documents.ts`).
 */
export function TemplateCard({ template, onUseTemplate }: TemplateCardProps): ReactElement {
  const { t } = useTemplatesT();

  return (
    <Card className={cn(styles['card'])}>
      <div className={cn(styles['header'])}>
        <span className={cn(styles['name'])}>{template.name}</span>
        <Badge tone="neutral" className={cn(styles['kindBadge'])}>
          {t(`templates:kind.${template.kind}`)}
        </Badge>
      </div>

      {template.description ? (
        <p className={cn(styles['description'])}>{template.description}</p>
      ) : (
        <p className={cn(styles['description'], styles['descriptionPlaceholder'])}>{t('templates:card.noDescription')}</p>
      )}

      <div className={cn(styles['actions'])}>
        <Button variant="secondary" onClick={() => onUseTemplate(template)} className={cn(styles['useButton'])}>
          {t('templates:action.useTemplate')}
        </Button>
      </div>
    </Card>
  );
}
